import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';
import { fetchNoticeById, fetchNotices, Notice } from '@/api/notice';
import NewsAttachIcon from '@/assets/icon/news-attach.svg?react';
import NewsKakaoIcon from '@/assets/icon/news-kakao.svg?react';
import { formatDateTime } from '@/utils/dateUtils';

const NoticeDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // ✅ id 변경될 때마다 강제 리페치 + 스크롤 맨 위
  useEffect(() => {
    window.scrollTo({ top: 0 });
    queryClient.invalidateQueries({ queryKey: ['notice', id] });
  }, [id, queryClient]);

  const {
    data: notice,
    isLoading,
    isError,
  } = useQuery<Notice>({
    queryKey: ['notice', id],
    queryFn: () => fetchNoticeById(Number(id)),
    enabled: !!id,
  });

  const { data: notices } = useQuery<Notice[]>({
    queryKey: ['notices'],
    queryFn: fetchNotices,
  });

  const recommended = [...(notices || [])]
    .filter((item) => item.id !== Number(id))
    .reverse()
    .slice(0, 4);

  if (isLoading) return <StatusText>불러오는 중...</StatusText>;
  if (isError || !notice) return <StatusText>공지사항을 불러올 수 없습니다.</StatusText>;

  const formattedDate = formatDateTime(notice.createdAt);

  return (
    <Outer>
      <Wrapper>
        <HeaderSection>
          <DateText>{formattedDate}</DateText>
          <TitleText>{notice.title}</TitleText>
        </HeaderSection>

        <Divider />

        <ContentSection dangerouslySetInnerHTML={{ __html: notice.content }} />

        <AttachmentSection>
          <IconRow>
            <NewsKakaoIcon
              style={{ cursor: 'pointer' }}
              onClick={() =>
                window.Kakao?.Link?.sendDefault({
                  objectType: 'feed',
                  content: {
                    title: notice.title,
                    description: notice.content.replace(/<[^>]+>/g, '').slice(0, 100),
                    imageUrl: 'https://www.hiadjuster.com/allbadeumIcon.svg',
                    link: {
                      mobileWebUrl: window.location.href,
                      webUrl: window.location.href,
                    },
                  },
                  buttons: [
                    {
                      title: '웹으로 보기',
                      link: {
                        mobileWebUrl: window.location.href,
                        webUrl: window.location.href,
                      },
                    },
                  ],
                })
              }
            />
            <NewsAttachIcon
              style={{ cursor: 'pointer' }}
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(window.location.href);
                  alert('링크가 복사되었습니다.');
                } catch (err) {
                  console.error('복사 실패:', err);
                }
              }}
            />
          </IconRow>
        </AttachmentSection>

        <ButtonRow>
          <BackButton onClick={() => navigate('/intro/news')}>
            <ButtonText>목록 보기</ButtonText>
          </BackButton>
        </ButtonRow>

        <Divider />

        <SubTitle>추천 콘텐츠</SubTitle>
        <RecommendationWrapper>
          {recommended.map((item, index) => (
            <RecommendItem key={item.id} onClick={() => navigate(`/intro/news/${item.id}`)}>
              <LeftBox>
                <RecommendIndex>{String(index + 1).padStart(2, '0')}</RecommendIndex>
              </LeftBox>
              <RightText>{item.title}</RightText>
            </RecommendItem>
          ))}
        </RecommendationWrapper>
      </Wrapper>
    </Outer>
  );
};

export default NoticeDetailPage;

// Styled Components
const Outer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`;

const Wrapper = styled.div`
  display: flex;
  width: 100%;
  max-width: 630px;

  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-top: 156px;
  padding: 0 16px;

  @media (max-width: 768px) {
    margin-top: 120px;
    padding: 0 12px;
    gap: 12px;
  }
`;

const HeaderSection = styled.div`
  align-self: stretch;
  text-align: center;
`;

const DateText = styled.div`
  color: var(--gray-ui_gray02, #252525);
  font-size: 18px;
  font-weight: 400;
  line-height: 120%;
  letter-spacing: -0.36px;

  @media (max-width: 768px) {
    font-size: 14px;
    letter-spacing: -0.28px;
  }
`;

const TitleText = styled.div`
  margin-top: 8px;
  color: var(--gray-ui_gray02, #252525);
  font-size: 32px;
  font-weight: 700;
  line-height: 130%;
  letter-spacing: -0.64px;

  @media (max-width: 768px) {
    font-size: 22px;
    letter-spacing: -0.4px;
  }
`;

const Divider = styled.hr`
  width: 100%;
  height: 0;
  border: none;
  border-top: 1px solid #000;
  margin: 50px 0;
`;

const ContentSection = styled.div`
  display: flex;
  padding: 16px 12px;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 24px;
  color: var(--gray-ui_gray03, #3a3a3a);
  font-size: 16px;
  font-weight: 400;
  line-height: 150%;
  letter-spacing: -0.32px;

  img {
    max-width: 100%;
    height: auto;
    display: block;
    margin: 0 auto;
  }

  figcaption {
    padding: 16px 12px;
    font-size: 14px;
    color: #666;
    text-align: center;

    a,
    span,
    div {
      max-width: 100%; /* ✅ 추가 */
      word-break: break-word; /* ✅ 단어 길어질 때 줄바꿈 */
      overflow-wrap: break-word;
      display: inline-block; /* ✅ 필요하면 줄바꿈 방지 */
    }
  }

  @media (max-width: 768px) {
    font-size: 14px;
    gap: 16px;
  }
`;

const AttachmentSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const IconRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;
`;

const ButtonRow = styled.div`
  display: flex;
  width: 275px;
  height: 60px;
  justify-content: flex-end;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
`;

const BackButton = styled.button`
  display: flex;
  padding: 16px 12px;
  justify-content: center;
  align-items: center;
  flex: 1;
  border-radius: 8px;
  background: var(--table-BG-table_bg02_hov, #f0f3fe);
  border: none;
  cursor: pointer;

  @media (max-width: 768px) {
    padding: 12px 8px;
  }
`;

const ButtonText = styled.span`
  padding: 0 16px;
  color: var(--Primary-brand_02, #4c84e3);
  font-size: 18px;
  font-weight: 700;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const SubTitle = styled.h3`
  align-self: flex-start;
  font-size: 32px;
  font-weight: 700;
  color: var(--gray-ui_gray02, #252525);
  margin-top: 10px;

  @media (max-width: 768px) {
    font-size: 20px;
    margin-top: 4px;
  }
`;

const RecommendationWrapper = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
  padding-bottom: 120px;
  gap: 12px;

  @media (max-width: 768px) {
    padding-bottom: 80px;
    gap: 8px;
  }
`;

const RecommendItem = styled.div`
  display: flex;
  width: 100%;
  padding: 16px 12px;
  align-items: center;
  gap: 10px;
  width: 100%;
  cursor: pointer;
  border-radius: 8px;
  text-align: start;
  &:hover {
    background: #f0f3fe;
  }
`;

const LeftBox = styled.div`
  display: flex;
  width: 56px;
  padding-right: 16px;
  justify-content: center;
  align-items: center;
`;

const RecommendIndex = styled.div`
  color: var(--gray-ui_gray05, #999);
  font-size: 18px;
  font-weight: 700;
  line-height: 120%;
  letter-spacing: -0.36px;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const RightText = styled.div`
  flex: 1;
  color: var(--gray-ui_gray03, #3a3a3a);
  font-size: 18px;
  font-weight: 400;
  line-height: 120%;
  letter-spacing: -0.36px;

  @media (max-width: 768px) {
    font-size: 14px;
    letter-spacing: -0.28px;
  }
`;

const StatusText = styled.div`
  width: 100%;
  text-align: center;
  padding: 100px 0;
  font-size: 16px;
  color: #999;
`;
